import { isFn } from './checkers';
import { ReactionStack, PendingScopeReactions, RawReactionsMap, PendingReactions, BatchCount, UntrackCount, ProxyRaw, RawNode, BatchScope, } from './environment';
import { concat } from './concat';
var ITERATION_KEY = Symbol('iteration key');
var addRawReactionsMap = function (target, key, reaction) {
    var reactionsMap = RawReactionsMap.get(target);
    if (reactionsMap) {
        var reactions = reactionsMap.get(key);
        if (reactions) {
            if (!reactions.has(reaction)) {
                reactions.add(reaction);
            }
        }
        else {
            reactionsMap.set(key, new Set([reaction]));
        }
        return reactionsMap;
    }
    else {
        var reactionsMap_1 = new Map([[key, new Set([reaction])]]);
        RawReactionsMap.set(target, reactionsMap_1);
        return reactionsMap_1;
    }
};
var addReactionsMaptoReaction = function (reaction, reactionsMap) {
    var bindSet = reaction._reactionsSet;
    if (bindSet) {
        if (!bindSet.has(reactionsMap)) {
            bindSet.add(reactionsMap);
        }
    }
    else {
        reaction._reactionsSet = new Set([reactionsMap]);
    }
    return bindSet;
};
var getReactionsFromTargetKey = function (target, key) {
    var _a;
    var reactionsMap = RawReactionsMap.get(target);
    var reactions = new Set();
    if (reactionsMap) {
        (_a = reactionsMap.get(key)) === null || _a === void 0 ? void 0 : _a.forEach(function (reaction) {
            if (!reactions.has(reaction)) {
                reactions.add(reaction);
            }
        });
    }
    return reactions;
};
var runReactions = function (target, key) {
    var reactions = getReactionsFromTargetKey(target, key);
    reactions.forEach(function (reaction) {
        if (reaction._isComputed) {
            reaction._scheduler(reaction);
        }
        else if (isScopeBatching()) {
            if (!PendingScopeReactions.has(reaction)) {
                PendingScopeReactions.add(reaction);
            }
        }
        else if (isBatching()) {
            if (!PendingReactions.has(reaction)) {
                PendingReactions.add(reaction);
            }
        }
        else {
            if (isFn(reaction._scheduler)) {
                reaction._scheduler(reaction);
            }
            else {
                reaction();
            }
        }
    });
};
var notifyObservers = function (operation) {
    var targetNode = RawNode.get(ProxyRaw.get(operation.target) || operation.target);
    var oldValueNode = RawNode.get(ProxyRaw.get(operation.oldValue) || operation.oldValue);
    var newValueNode = RawNode.get(ProxyRaw.get(operation.value) || operation.value);
    if (targetNode) {
        var getChange_1 = function () {
            return {
                get path() {
                    return concat(targetNode.path, operation.key);
                },
                type: operation.type,
                key: operation.key,
                value: operation.value,
                oldValue: operation.oldValue,
            };
        };
        if (oldValueNode && operation.type === 'set') {
            oldValueNode.observers.forEach(function (fn) { return fn(getChange_1()); });
            oldValueNode.deepObservers.forEach(function (fn) { return fn(getChange_1()); });
            if (newValueNode) {
                newValueNode.observers = oldValueNode.observers;
                newValueNode.deepObservers = oldValueNode.deepObservers;
            }
        }
        if (oldValueNode && operation.type === 'delete') {
            oldValueNode.observers = [];
            oldValueNode.deepObservers = [];
        }
        targetNode.observers.forEach(function (fn) { return fn(getChange_1()); });
        targetNode.deepObservers.forEach(function (fn) { return fn(getChange_1()); });
        var parent_1 = targetNode.parent;
        while (!!parent_1) {
            parent_1.deepObservers.forEach(function (fn) { return fn(getChange_1()); });
            parent_1 = parent_1.parent;
        }
    }
};
export var bindTargetKeyWithCurrentReaction = function (operation) {
    var key = operation.key, type = operation.type, target = operation.target;
    if (type === 'iterate') {
        key = ITERATION_KEY;
    }
    var current = ReactionStack[ReactionStack.length - 1];
    if (isUntracking())
        return;
    if (current) {
        addReactionsMaptoReaction(current, addRawReactionsMap(target, key, current));
    }
};
export var bindComputedReactions = function (reaction) {
    if (isFn(reaction)) {
        var current = ReactionStack[ReactionStack.length - 1];
        if (current) {
            var computeds = current._computedsSet;
            if (computeds) {
                if (!computeds.has(reaction)) {
                    computeds.add(reaction);
                }
            }
            else {
                current._computedsSet = new Set([reaction]);
            }
        }
    }
};
export var suspendComputedReactions = function (reaction) {
    var computeds = reaction._computedsSet;
    if (computeds) {
        computeds.forEach(function (reaction) {
            var reactions = getReactionsFromTargetKey(reaction._context, reaction._property);
            if (reactions.size === 0) {
                disposeBindingReactions(reaction);
                reaction._dirty = true;
            }
        });
    }
};
export var runReactionsFromTargetKey = function (operation) {
    var key = operation.key, type = operation.type, target = operation.target, oldTarget = operation.oldTarget;
    notifyObservers(operation);
    if (type === 'clear') {
        oldTarget.forEach(function (_, key) {
            runReactions(target, key);
        });
    }
    else {
        runReactions(target, key);
    }
    if (type === 'add' || type === 'delete' || type === 'clear') {
        key = Array.isArray(target) ? 'length' : ITERATION_KEY;
        runReactions(target, key);
    }
};
export var hasRunningReaction = function () {
    return ReactionStack.length > 0;
};
export var releaseBindingReactions = function (reaction) {
    var bindingSet = reaction._reactionsSet;
    if (bindingSet) {
        bindingSet.forEach(function (reactionsMap) {
            reactionsMap.forEach(function (reactions) {
                reactions.delete(reaction);
            });
        });
    }
    delete reaction._reactionsSet;
};
export var disposeBindingReactions = function (reaction) {
    releaseBindingReactions(reaction);
    suspendComputedReactions(reaction);
};
export var batchStart = function () {
    BatchCount.value++;
};
export var batchEnd = function () {
    BatchCount.value--;
    if (BatchCount.value === 0) {
        excutePendingReactions();
    }
};
export var batchScopeStart = function () {
    BatchScope.value = true;
};
export var batchScopeEnd = function () {
    BatchScope.value = false;
    PendingScopeReactions.forEach(function (reaction) {
        PendingScopeReactions.delete(reaction);
        if (isFn(reaction._scheduler)) {
            reaction._scheduler(reaction);
        }
        else {
            reaction();
        }
    });
};
export var untrackStart = function () {
    UntrackCount.value++;
};
export var untrackEnd = function () {
    UntrackCount.value--;
};
export var isBatching = function () { return BatchCount.value > 0; };
export var isScopeBatching = function () { return BatchScope.value; };
export var isUntracking = function () { return UntrackCount.value > 0; };
export var excutePendingReactions = function () {
    PendingReactions.forEach(function (reaction) {
        PendingReactions.delete(reaction);
        if (isFn(reaction._scheduler)) {
            reaction._scheduler(reaction);
        }
        else {
            reaction();
        }
    });
};
